import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CognitoUser, AuthenticationDetails, CognitoUserAttribute } from 'amazon-cognito-identity-js';

import userPool from '../auth/UserPool';
import { Make_Authorized_API_Call } from '../api/apiClient';

interface StatusState {
  Is_Loading: boolean;          // Tim-Combo: Pascal_Case for auth properties
  Auth_Status: boolean;          // Tim-Combo: Pascal_Case for auth properties
  Email_Address: string | null;  // Tim-Combo: Pascal_Case for auth properties
  Error_Message: string | null;  // Tim-Combo: Pascal_Case for auth properties
  user_id: string | null;        // Keep snake_case (family-specific, database field)
  family_id: string | null;      // Keep snake_case (family-specific, database field)
  family_code: string | null;    // Keep snake_case (family-specific, database field)
  idToken: string | null;        // Keep as-is
  accessToken: string | null;    // Added: missing token storage
}

interface StatusContextValue extends StatusState {
  authenticate: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  needsNewPassword: boolean;
  register: (email: string, password: string, name: string, familyName?: string, birthDate?: string) => Promise<{ needsVerification: boolean; email: string; username: string }>;
  confirmRegistration: (username: string, code: string) => Promise<void>;
  resendVerificationCode: (username: string) => Promise<void>;
  logout: () => void;
  generateFamilyCode: () => string;
  updateFamilyCode: (code: string) => void;
  isAuthenticated: boolean;
}

const StatusContext = createContext<StatusContextValue | null>(null);

export const useStatus = () => {
  const context = useContext(StatusContext);
  if (!context) {
    throw new Error('useStatus must be used within a StatusProvider');
  }
  return context;
};

interface StatusProviderProps {
  children: React.ReactNode;
}

export const StatusProvider: React.FC<StatusProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [state, setState] = useState<StatusState>({
    Is_Loading: false,
    Auth_Status: false,
    Email_Address: null,
    Error_Message: null,
    user_id: null,
    family_id: null,
    family_code: null,
    idToken: null,
    accessToken: null
  });

  // Track NEW_PASSWORD_REQUIRED challenge state
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const cognitoUserRef = useRef<CognitoUser | null>(null);
  const pendingEmailRef = useRef<string | null>(null);

  // Family code generation utility (exclude confusing characters)
  const generateFamilyCode = useCallback(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No O/0, I/1
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }, []);

  // Update family code in state
  const updateFamilyCode = useCallback((code: string) => {
    setState(prev => ({ ...prev, family_code: code }));
  }, []);

  // Check user status - fetch profile and entitlements from database
  const Check_User_Status = useCallback(async (email: string, idToken: string) => {
    console.log('[Auth] Fetching user profile from /users/profile');

    try {
      // Use Make_Authorized_API_Call (Tim-Combo pattern - no raw fetch)
      const response = await Make_Authorized_API_Call('/users/profile', 'GET');

      console.log('[Auth] Got user profile:', response);

      // Extract user data from response (assuming standard API response format)
      const userData = response.data?.Records?.[0] || response.data || response;

      setState(prev => ({
        ...prev,
        Auth_Status: true,
        Is_Loading: false,
        Email_Address: userData.email || email,
        user_id: userData.user_id, // Database user_id (UUID)
        family_id: userData.family_id, // Database family_id (UUID)
        family_code: userData.family_code || null,
        idToken // Keep idToken from authenticate
      }));

      console.log('[Auth] State updated with database user data');
    } catch (profileError) {
      console.error('[Auth] Failed to fetch user profile:', profileError);
      // Still mark as authenticated but without user_id/family_id
      setState(prev => ({
        ...prev,
        Auth_Status: true,
        Is_Loading: false,
        Email_Address: email,
        Error_Message: 'Failed to load user profile'
      }));
    }
  }, []);

  const authenticate = useCallback(async (email: string, password: string) => {
    if (!userPool) {
      console.error('[Auth] Cognito not configured');
      setState(prev => ({ ...prev, Error_Message: 'Cognito not configured' }));
      return;
    }

    console.log('[Auth] Starting authentication for:', email);
    setState(prev => ({ ...prev, Is_Loading: true, Error_Message: null }));

    try {
      const authenticationData = { Username: email, Password: password };
      const authenticationDetails = new AuthenticationDetails(authenticationData);

      const userData = { Username: email, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      // Authenticate with Cognito
      await new Promise<void>((resolve, reject) => {
        console.log('[Auth] Calling Cognito authenticateUser...');
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: async (result) => {
            console.log('[Auth] Cognito authentication SUCCESS');
            const idToken = result.getIdToken().getJwtToken();
            const accessToken = result.getAccessToken().getJwtToken();

            // FIXED: Store tokens with primary key 'token' for apiClient compatibility
            localStorage.setItem('token', idToken);           // Primary key (apiClient.ts reads this)
            localStorage.setItem('idToken', idToken);         // Backward compatibility
            localStorage.setItem('accessToken', accessToken); // Access token storage
            console.log('[Auth] Tokens stored in localStorage (token, idToken, accessToken)');

            // Update state with both tokens
            setState(prev => ({ ...prev, idToken, accessToken }));

            // Use Check_User_Status to fetch user profile
            await Check_User_Status(email, idToken);

            // Wait for state update to be processed before resolving
            setTimeout(() => {
              console.log('[Auth] Resolving authenticate promise');
              resolve();
            }, 100);
          },
          newPasswordRequired: (userAttributes) => {
            console.log('[Auth] NEW_PASSWORD_REQUIRED challenge received');
            // Store the cognitoUser so completeNewPassword can use it
            cognitoUserRef.current = cognitoUser;
            pendingEmailRef.current = email;
            setNeedsNewPassword(true);
            setState(prev => ({ ...prev, Is_Loading: false }));
            resolve(); // Don't reject - this is a valid flow, not an error
          },
          onFailure: (err) => {
            console.error('[Auth] Cognito auth FAILURE:', err);
            reject(err);
          }
        });
      });

    } catch (error: any) {
      console.error('Authentication failed:', error);

      // Parse error message for user-friendly display
      let errorMessage = error.message || 'Authentication failed';
      if (error.code === 'UserNotConfirmedException') {
        errorMessage = 'Email not verified. Please check your email for the verification code.';
        // Add flags so UI can show verification modal
        error.needsVerification = true;
        error.email = email;
      } else if (error.code === 'NotAuthorizedException') {
        errorMessage = 'Incorrect email or password';
      } else if (error.code === 'UserNotFoundException') {
        errorMessage = 'No account found with this email';
      }

      setState(prev => ({
        ...prev,
        Is_Loading: false,
        Auth_Status: false,
        Error_Message: errorMessage
      }));
      throw error;
    }
  }, [Check_User_Status]);

  const completeNewPassword = useCallback(async (newPassword: string) => {
    const cognitoUser = cognitoUserRef.current;
    const email = pendingEmailRef.current;

    if (!cognitoUser || !email) {
      throw new Error('No pending password challenge');
    }

    console.log('[Auth] Completing new password challenge for:', email);
    setState(prev => ({ ...prev, Is_Loading: true, Error_Message: null }));

    try {
      await new Promise<void>((resolve, reject) => {
        cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
          onSuccess: async (result) => {
            console.log('[Auth] New password set successfully');
            const idToken = result.getIdToken().getJwtToken();
            const accessToken = result.getAccessToken().getJwtToken();

            localStorage.setItem('token', idToken);
            localStorage.setItem('idToken', idToken);
            localStorage.setItem('accessToken', accessToken);

            setState(prev => ({ ...prev, idToken, accessToken }));
            setNeedsNewPassword(false);
            cognitoUserRef.current = null;

            await Check_User_Status(email, idToken);

            setTimeout(() => resolve(), 100);
          },
          onFailure: (err) => {
            console.error('[Auth] Complete new password FAILURE:', err);
            reject(err);
          }
        });
      });

      pendingEmailRef.current = null;
    } catch (error: any) {
      console.error('Complete new password failed:', error);
      let errorMessage = error.message || 'Failed to set new password';
      if (error.code === 'InvalidPasswordException') {
        errorMessage = 'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
      }
      setState(prev => ({ ...prev, Is_Loading: false, Error_Message: errorMessage }));
      throw error;
    }
  }, [Check_User_Status]);

  const register = useCallback(async (email: string, password: string, name: string, familyName?: string, birthDate?: string) => {
    if (!userPool) {
      console.error('[Register] Cognito not configured');
      setState(prev => ({ ...prev, Error_Message: 'Cognito not configured' }));
      throw new Error('Cognito not configured');
    }

    console.log('[Register] Starting registration for:', email);
    setState(prev => ({ ...prev, Is_Loading: true, Error_Message: null }));

    try {
      // Only store minimal attributes in Cognito (required for OIDC compatibility with Google/Apple)
      // All other user data goes in PostgreSQL users table where we can query it
      const attributeList: CognitoUserAttribute[] = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: email
        }),
        new CognitoUserAttribute({
          Name: 'name',
          Value: name
        })
      ];

      // Store birth_date in sessionStorage for use after email verification
      // birth_date will be sent to the backend via /auth/register after confirmation
      if (birthDate) {
        sessionStorage.setItem('user_birth_date', birthDate);
        console.log('[Register] Stored birth_date in sessionStorage:', birthDate);
      }

      // Note: familyName will be stored in users table via post-signup Lambda trigger

      // Sign up with Cognito
      if (!userPool) {
        throw new Error('User pool not initialized');
      }

      // For email alias pools, generate a unique username that's NOT an email format
      // This prevents "Username cannot be of email format" error
      const username = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      await new Promise<void>((resolve, reject) => {
        console.log('[Register] Calling Cognito signUp with username:', username);
        userPool!.signUp(username, password, attributeList, [], (err, result) => {
          if (err) {
            console.error('[Register] Cognito signUp FAILURE:', err);
            reject(err);
            return;
          }

          console.log('[Register] Cognito signUp SUCCESS:', result);
          resolve();
        });
      });

      setState(prev => ({ ...prev, Is_Loading: false }));
      console.log('[Register] Registration completed, verification email sent');

      return { needsVerification: true, email, username };

    } catch (error: any) {
      console.error('Registration failed:', error);

      // Parse Cognito error messages for user-friendly display
      let errorMessage = 'Registration failed';
      if (error.code === 'UsernameExistsException') {
        errorMessage = 'This email is already registered. Please sign in or use a different email.';
      } else if (error.code === 'InvalidPasswordException') {
        errorMessage = 'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
      } else if (error.code === 'InvalidParameterException') {
        errorMessage = 'Invalid email or password format';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setState(prev => ({
        ...prev,
        Is_Loading: false,
        Error_Message: errorMessage
      }));
      throw error;
    }
  }, []);

  const confirmRegistration = useCallback(async (username: string, code: string) => {
    if (!userPool) {
      console.error('[Confirm] Cognito not configured');
      setState(prev => ({ ...prev, Error_Message: 'Cognito not configured' }));
      throw new Error('Cognito not configured');
    }

    console.log('[Confirm] Confirming registration for username:', username);
    setState(prev => ({ ...prev, Is_Loading: true, Error_Message: null }));

    try {
      const userData = { Username: username, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      // Confirm registration with verification code
      await new Promise<void>((resolve, reject) => {
        console.log('[Confirm] Calling Cognito confirmRegistration...');
        cognitoUser.confirmRegistration(code, true, (err, result) => {
          if (err) {
            console.error('[Confirm] Cognito confirmRegistration FAILURE:', err);
            reject(err);
            return;
          }

          console.log('[Confirm] Cognito confirmRegistration SUCCESS:', result);
          resolve();
        });
      });

      console.log('[Confirm] Email verified successfully');
      setState(prev => ({ ...prev, Is_Loading: false }));

    } catch (error: any) {
      console.error('Confirmation failed:', error);

      // Parse Cognito error messages
      let errorMessage = 'Verification failed';
      if (error.code === 'CodeMismatchException') {
        errorMessage = 'Invalid verification code';
      } else if (error.code === 'ExpiredCodeException') {
        errorMessage = 'Verification code has expired';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setState(prev => ({
        ...prev,
        Is_Loading: false,
        Error_Message: errorMessage
      }));
      throw error;
    }
  }, []);

  const resendVerificationCode = useCallback(async (username: string) => {
    if (!userPool) {
      console.error('[Resend] Cognito not configured');
      throw new Error('Cognito not configured');
    }

    console.log('[Resend] Resending verification code for username:', username);
    setState(prev => ({ ...prev, Is_Loading: true, Error_Message: null }));

    try {
      const userData = { Username: username, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      await new Promise<void>((resolve, reject) => {
        cognitoUser.resendConfirmationCode((err, result) => {
          if (err) {
            console.error('[Resend] Failed:', err);
            reject(err);
            return;
          }
          console.log('[Resend] Success:', result);
          resolve();
        });
      });

      setState(prev => ({ ...prev, Is_Loading: false }));
      console.log('[Resend] Verification code sent successfully');
    } catch (error: any) {
      console.error('Resend failed:', error);
      setState(prev => ({
        ...prev,
        Is_Loading: false,
        Error_Message: error.message || 'Failed to resend verification code'
      }));
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    // FIXED: Clear BOTH tokens (Tim-Combo pattern)
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token'); // Legacy support
    localStorage.removeItem('userData');
    setState({
      Is_Loading: false,
      Auth_Status: false,
      Email_Address: null,
      Error_Message: null,
      user_id: null,
      family_id: null,
      family_code: null,
      idToken: null,
      accessToken: null
    });
    navigate('/');
  }, [navigate]);

  const value: StatusContextValue = {
    ...state,
    authenticate,
    completeNewPassword,
    needsNewPassword,
    register,
    confirmRegistration,
    resendVerificationCode,
    logout,
    generateFamilyCode,
    updateFamilyCode,
    isAuthenticated: state.Auth_Status && !!state.idToken
  };

  return <StatusContext.Provider value={value}>{children}</StatusContext.Provider>;
};
